// Drops one trailing slash only (`/a/b/` is `/a/b`), so an empty segment as in `/a//b` matches no route segment.
// A trailing `/*` compares nothing below it, so it takes empty segments too.
const splitPath = (path: string): string[] => {
    const segments = path.split('/').slice(1);
    return segments.at(-1) === '' ? segments.slice(0, -1) : segments;
};

/**
 * Matches a pathname against one widget route outside the route tree: widget routes arrive at runtime as tenant
 * configuration. `pattern` is in the manifest grammar (`WIDGET_ROUTE_RE` in `@luminovo/apps-types`, enforced by
 * `slotAssignmentsSchema` before matching) and nothing wider: static segments, matched case-insensitively as the
 * product's routes are; `:param`, exactly one non-empty segment; an optional trailing `/*`, anything below,
 * including nothing. Params come back still encoded, under their declared names; the wildcard remainder isn't
 * captured. `undefined`: the route doesn't claim the pathname.
 */
export function matchWidgetRoute(pattern: string, pathname: string): Record<string, string> | undefined {
    const routeSegments = splitPath(pattern);
    const hasRest = routeSegments.at(-1) === '*';
    if (hasRest) routeSegments.pop();

    const pathSegments = splitPath(pathname);
    const claimsLength = hasRest
        ? pathSegments.length >= routeSegments.length
        : pathSegments.length === routeSegments.length;
    if (!claimsLength) return undefined;

    const params: Record<string, string> = {};
    for (const [index, routeSegment] of routeSegments.entries()) {
        const segment = pathSegments[index];
        if (routeSegment.startsWith(':')) {
            if (segment === '') return undefined;
            params[routeSegment.slice(1)] = segment;
        } else if (routeSegment.toLowerCase() !== segment.toLowerCase()) {
            return undefined;
        }
    }
    return params;
}
